import type { Student } from '../types';
import { safeParseJson } from '../utils/safeParseJson';
import type { TeachCohort, TeachLastRandomPick, TeachTimerState } from './teachSession';
import { tickCountdown } from './teachSession';

/** Timer, cohort, random pick, and roster UI — survives refresh on this device. */
export const DG_TEACH_SESSION_V1 = 'dg_teach_session_v1';

export type PersistedTeachRosterUi = {
  courseOpenId: string | null;
  selectedAssignmentId: string;
  selectedStudentIds: string[];
};

export type TeachSessionSnapshotV1 = {
  v: 1;
  timer: TeachTimerState;
  cohort: TeachCohort | null;
  lastRandomPick: TeachLastRandomPick;
  rosterUi: PersistedTeachRosterUi | null;
};

function isStudent(x: unknown): x is Student {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.name === 'string';
}

function isTeachCohort(x: unknown): x is TeachCohort {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (
    typeof o.courseId !== 'string' ||
    typeof o.courseName !== 'string' ||
    typeof o.assignmentId !== 'string' ||
    typeof o.assignmentTitle !== 'string' ||
    !Array.isArray(o.students)
  ) {
    return false;
  }
  return (o.students as unknown[]).every(isStudent);
}

function isTeachTimerState(x: unknown): x is TeachTimerState {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  const okStatus = ['idle', 'running', 'paused', 'ended'].includes(o.status as string);
  return (
    okStatus &&
    typeof o.durationMs === 'number' &&
    typeof o.remainingMs === 'number' &&
    (o.endsAtMs === null || typeof o.endsAtMs === 'number') &&
    (o.startedAtMs === null || typeof o.startedAtMs === 'number')
  );
}

function isLastPick(x: unknown): x is NonNullable<TeachLastRandomPick> {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.studentId === 'string' && typeof o.studentName === 'string';
}

export function normalizeLastPick(
  pick: TeachLastRandomPick,
  cohort: TeachCohort | null
): TeachLastRandomPick {
  if (!pick || !cohort?.students?.length) return null;
  const ok = cohort.students.some((s) => s.id === pick.studentId);
  return ok ? pick : null;
}

export function loadTeachSessionSnapshot(): TeachSessionSnapshotV1 | null {
  if (typeof window === 'undefined') return null;
  const raw = safeParseJson<unknown>(window.localStorage.getItem(DG_TEACH_SESSION_V1), null);
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  if (rec.v !== 1) return null;
  if (!isTeachTimerState(rec.timer)) return null;

  let timer = rec.timer;
  if (timer.status === 'running') {
    timer = tickCountdown(timer, Date.now());
  }

  const cohort = rec.cohort === null ? null : isTeachCohort(rec.cohort) ? rec.cohort : null;

  let lastRandomPick: TeachLastRandomPick = null;
  if (rec.lastRandomPick === null) {
    lastRandomPick = null;
  } else if (isLastPick(rec.lastRandomPick)) {
    lastRandomPick = normalizeLastPick(rec.lastRandomPick, cohort);
  } else {
    lastRandomPick = null;
  }

  let rosterUi: PersistedTeachRosterUi | null = null;
  if (rec.rosterUi && typeof rec.rosterUi === 'object') {
    const r = rec.rosterUi as Record<string, unknown>;
    const courseOpenId =
      r.courseOpenId === null || typeof r.courseOpenId === 'string' ? r.courseOpenId : null;
    const selectedAssignmentId =
      typeof r.selectedAssignmentId === 'string' ? r.selectedAssignmentId : '';
    const ids = r.selectedStudentIds;
    const selectedStudentIds = Array.isArray(ids)
      ? ids.filter((id): id is string => typeof id === 'string')
      : [];
    rosterUi = { courseOpenId, selectedAssignmentId, selectedStudentIds };
  }

  return {
    v: 1,
    timer,
    cohort,
    lastRandomPick,
    rosterUi,
  };
}

export function saveTeachSessionSnapshot(snapshot: TeachSessionSnapshotV1): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DG_TEACH_SESSION_V1, JSON.stringify(snapshot));
  } catch {
    /* quota */
  }
}
